/**
 * O bot no chat entre pessoas (ADR-0017): a resposta a chegar, «Parar», as ações a confirmar e o que o bot pode fazer.
 *
 * As respostas a meio vivem na cache do TanStack Query (uma entrada por conversa), escritas pelos eventos em tempo real
 * com `setQueryData` — nunca se pede nada por lote. Só quando falta um lote se retoma por REST (`GET /bot-runs/{id}`).
 */

import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import { applyDelta, applyDone, applyResume, applyStep, runsFor, type LiveBotRun, type LiveBotRuns } from "@/lib/chat/bot-runs";
import type { ChatEventOf } from "@/lib/chat/realtime-events";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import type { Schemas } from "@/types/api";
import { emptyWindow, upsertMessage, type ChatMessage, type MessageWindow } from "@/lib/chat/message-window";
import { CHAT_ROOT, chatKeys } from "./people-chat-keys";

export type BotAction = Schemas["ChatBotActionRead"];

export const botKeys = {
  runs: (conversationId: string) => [...CHAT_ROOT, "bot-runs", conversationId] as const,
  actions: (conversationId: string) => [...CHAT_ROOT, "bot-actions", conversationId] as const,
  capabilities: () => [...CHAT_ROOT, "bot-capabilities"] as const,
};

type BotEvent = ChatEventOf<"bot.step"> | ChatEventOf<"bot.delta"> | ChatEventOf<"bot.done">;

const resuming = new Set<string>();

async function resume(qc: QueryClient, conversationId: string, runId: string): Promise<void> {
  if (resuming.has(runId)) return;
  resuming.add(runId);
  try {
    const run = await api.get("/chat/bot-runs/{run_id}", { path: { run_id: runId } });
    qc.setQueryData<LiveBotRuns>(botKeys.runs(conversationId), (runs) => (runs ? applyResume(runs, runId, run.partial_text) : runs));
  } catch {
    // Sem retoma: o texto final chega na mesma como mensagem quando o bot acabar.
  } finally {
    resuming.delete(runId);
  }
}

/** Um evento do bot entra na cache. Chamado por `applyEvent` (`use-chat-realtime`). */
export function applyBotEvent(qc: QueryClient, event: BotEvent): void {
  const key = botKeys.runs(event.conversation_id);
  if (event.type === "bot.done") {
    qc.setQueryData<LiveBotRuns>(key, (runs) => (runs ? applyDone(runs, event.run_id) : runs));
    return;
  }
  const next = qc.setQueryData<LiveBotRuns>(key, (runs) =>
    event.type === "bot.step" ? applyStep(runs ?? {}, event) : applyDelta(runs ?? {}, event),
  );
  if (next?.[event.run_id]?.needsResume) void resume(qc, event.conversation_id, event.run_id);
}

/**
 * O bot está a responder nesta conversa (em qualquer thread)? Serve a barra lateral: dá para ter várias conversas a gerar ao
 * mesmo tempo e saltar entre elas; a que está a trabalhar mostra-o, e quando acaba chega como mensagem por ler.
 */
export function useIsBotWorking(conversationId: string): boolean {
  const { data } = useQuery<LiveBotRuns, Error, boolean>({
    queryKey: botKeys.runs(conversationId),
    queryFn: () => ({}),
    initialData: {},
    enabled: false,
    select: (runs) => Object.values(runs).some((run) => run.status === "a_correr"),
  });
  return data;
}

/** As respostas do bot a meio nesta janela (a conversa principal, `threadRootId = null`, ou uma thread). */
export function useLiveBotRuns(conversationId: string, threadRootId: string | null): LiveBotRun[] {
  const { data } = useQuery<LiveBotRuns>({
    queryKey: botKeys.runs(conversationId),
    queryFn: () => ({}),
    initialData: {},
    // Só os eventos escrevem aqui: um refetch apagaria o texto a meio.
    enabled: false,
  });
  return runsFor(data, threadRootId);
}

export function useStopBotRun() {
  return useMutationWithToast<Schemas["ChatBotRunRead"], Error, string>({
    mutationFn: (runId) => api.post("/chat/bot-runs/{run_id}/stop", { path: { run_id: runId } }),
    errorMessage: (error) => error.message || "Não foi possível parar o bot.",
  });
}

/** As ações propostas pelo bot nesta conversa (o cartão «Confirmo / Cancelar»). Só se pede com mensagens do bot à vista. */
export function useBotActions(conversationId: string, enabled: boolean) {
  return useQuery({
    queryKey: botKeys.actions(conversationId),
    queryFn: () => api.get("/chat/conversations/{conversation_id}/bot-actions", { path: { conversation_id: conversationId } }),
    enabled,
    staleTime: 30_000,
  });
}

export function useDecideBotAction(conversationId: string) {
  const qc = useQueryClient();
  return useMutationWithToast<BotAction, Error, { actionId: string; confirm: boolean }>({
    mutationFn: ({ actionId, confirm }) => api.post("/chat/bot-actions/{action_id}/decide", { path: { action_id: actionId }, body: { confirm } }),
    onSuccessCallback: (saved) => {
      qc.setQueryData<BotAction[]>(botKeys.actions(conversationId), (list) => list?.map((a) => (a.id === saved.id ? saved : a)));
    },
    errorMessage: (error) => error.message || "Não foi possível decidir a ação.",
  });
}

/** O que o bot pode fazer por esta pessoa (clicar no nome do bot). */
export function useBotCapabilities(enabled: boolean) {
  return useQuery({
    queryKey: botKeys.capabilities(),
    queryFn: () => api.get("/chat/bot/capabilities"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** «Limpar a conversa» com o bot (revisão #15): o texto e os anexos saem; as mensagens chegam como eliminadas. */
export function useClearBotConversation(conversationId: string) {
  return useMutationWithToast<Schemas["ChatBotDirectCleared"], Error, void>({
    mutationFn: () => api.post("/chat/conversations/{conversation_id}/bot-clear", { path: { conversation_id: conversationId } }),
    successMessage: "Conversa com o bot limpa.",
    errorMessage: (error) => error.message || "Não foi possível limpar a conversa.",
  });
}

/** Descarrega as minhas mensagens (revisão #5: só das conversas que leio agora) num ficheiro JSON. */
export async function downloadMyChatExport(): Promise<number> {
  const data = await api.get("/chat/export");
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `chat-as-minhas-mensagens-${data.generated_at.slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return data.messages.length;
}

/** A opinião (👍/👎) sobre uma resposta do bot: mostra-se já e acerta com a resposta do servidor. */
export function useMessageFeedback() {
  const qc = useQueryClient();
  return useMutationWithToast<ChatMessage, Error, { message: ChatMessage; value: "up" | "down" | null; comment?: string | null }, { previous: ChatMessage }>({
    mutationFn: ({ message, value, comment }) =>
      api.put("/chat/conversations/{conversation_id}/messages/{message_id}/feedback", {
        path: { conversation_id: message.conversation_id, message_id: message.id },
        body: { value, comment: comment ?? null },
      }),
    errorMessage: (error) => error.message || "Não foi possível guardar a opinião.",
    onErrorCallback: (_error, vars, context) => {
      const key = chatKeys.window(vars.message.conversation_id, vars.message.thread_root_id ?? null);
      if (context?.previous) qc.setQueryData<MessageWindow>(key, (w) => upsertMessage(w ?? emptyWindow(), context.previous));
    },
    mutationOptions: {
      onMutate: ({ message, value }) => {
        const key = chatKeys.window(message.conversation_id, message.thread_root_id ?? null);
        qc.setQueryData<MessageWindow>(key, (w) => upsertMessage(w ?? emptyWindow(), { ...message, my_feedback: value }));
        return { previous: message };
      },
    },
    onSuccessCallback: (saved) => {
      const key = chatKeys.window(saved.conversation_id, saved.thread_root_id ?? null);
      qc.setQueryData<MessageWindow>(key, (w) => upsertMessage(w ?? emptyWindow(), saved));
    },
  });
}

/** «Gerar de novo»: o bot responde outra vez à mesma pergunta; a resposta nova chega pelo tempo real. */
export function useRegenerateBotReply() {
  return useMutationWithToast<Schemas["ChatBotRunRead"], Error, ChatMessage>({
    mutationFn: (message) =>
      api.post("/chat/conversations/{conversation_id}/messages/{message_id}/regenerate", {
        path: { conversation_id: message.conversation_id, message_id: message.id },
      }),
    errorMessage: (error) => error.message || "Não foi possível gerar de novo.",
  });
}
